<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import { toast } from "@/common/utils/toast";

import { regionList } from "@/common/api/rest/region";
import {
  consoleRegionList,
  consoleRegionCreate,
  consoleRegionDel,
  consoleRegionPatch,
} from "@/common/api/rest/console/region";

import type { RegionType } from "@/common/types/region";
import type {
  ConsoleRegionType,
  ConsoleRegionPayloadType,
} from "@/common/types/console/region";

import { baseDate, baseInput } from "@/common/components";
import { sidebarListCheckbox, svgIcon } from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  accessoryId: string;
}>();

const regions = ref<RegionType[]>([]);
const consoleRegions = ref<ConsoleRegionType[]>([]);

const loading = ref(false);
const showSidebar = ref(false);

const added = computed(() =>
  consoleRegions.value.map((item) => item.region_id),
);

async function fetchList() {
  await regionList({
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        regions.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

async function fetchItems() {
  await consoleRegionList(props.accessoryId, {
    full: true,
    sort: "ASC",
    sort_by: "name",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        consoleRegions.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function addItem(item: RegionType) {
  const payload: ConsoleRegionPayloadType = {
    region_id: item.region_id,
    release_year: null,
    bar_code: null,
    manufacturer_code: null,
  };

  await consoleRegionCreate(props.accessoryId, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchItems();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function removeItem(data: RegionType) {
  const item = consoleRegions.value.find(
    (item) => item.region_id === data.region_id,
  );

  if (!item) return;

  await consoleRegionDel(item.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchItems();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function patchItem(itemId: string, key: string, value: boolean) {
  const payload = {
    [key]: value,
  };

  await consoleRegionPatch(itemId, payload)
    .then((response) => {
      const { success, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function openSidebar() {
  await fetchList();
  showSidebar.value = true;
}

function closeSidebar() {
  showSidebar.value = false;
}

const debouncePatch = debounce(patchItem, 500);

onMounted(() => {
  fetchItems();
});
</script>

<template>
  <section class="tab-details">
    <div class="list">
      <div class="header">
        <div class="title">
          {{ t("collection.labels.regions") }}
        </div>
        <button class="btn btn-default w-8 h-8 p-0" @click="openSidebar()">
          <svg-icon icon="bars" size="1rem" />
        </button>
      </div>
      <div class="items">
        <template v-for="(item, itemIndex) in consoleRegions" :key="itemIndex">
          <div class="item">
            <div class="item-header">
              <div class="name">
                {{ item.name }}
              </div>
              <button
                class="btn btn-default w-5 h-5 p-0"
                @click="removeItem(item as any)"
              >
                <svg-icon icon="close" size="0.75rem" />
              </button>
            </div>
            <div class="item-content">
              <base-date
                v-model="item.release_year"
                :value="item.release_year"
                value-mask="YYYY-MM-DD"
                placeholder="dd/mm/yyyy"
                @update:modelValue="
                  patchItem(item.item_id, 'release_year', $event)
                "
              >
                <template #label>
                  {{ t("collection.labels.release_date") }}
                </template>
              </base-date>

              <base-input
                v-model="item.bar_code"
                :value="item.bar_code"
                placeholder="Ex: 12345678901234567890"
                required
                @update:modelValue="
                  debouncePatch(item.item_id, 'bar_code', $event)
                "
              >
                <template #label> Código de barras</template>
              </base-input>

              <base-input
                v-model="item.manufacturer_code"
                :value="item.manufacturer_code"
                placeholder="Ex: 12345"
                required
                @update:modelValue="
                  debouncePatch(item.item_id, 'manufacturer_code', $event)
                "
              >
                <template #label> Código de fabricante</template>
              </base-input>
            </div>
          </div>
        </template>

        <template v-if="!consoleRegions.length">
          <div class="empty-data">
            {{ t("collection.texts.empty_items") }}
          </div>
        </template>
      </div>
    </div>

    <template v-if="showSidebar">
      <sidebar-list-checkbox
        id="region"
        :width="$store.getters.mobile ? '90vw' : '40rem'"
        :open="showSidebar"
        :loading="loading"
        :outside-close="true"
        :title="t('collection.labels.regions')"
        :list="regions"
        field-id="region_id"
        field-option="name"
        :added="added"
        @close="closeSidebar()"
        @add:item="addItem"
        @remove:item="removeItem"
      />
    </template>
  </section>
</template>

<style lang="scss" scoped>
.tab-details {
  display: flex;
  flex-direction: column;

  .list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border-block-end: 1px solid var(--border);
    padding-block-end: 1rem;
    margin-block-end: 1rem;

    .header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin-inline: 1rem;

      .title {
        font-size: 1.1rem;
        font-weight: 500;
      }
    }

    .items {
      display: flex;
      flex-wrap: wrap;
      padding-block: 1rem;
      padding-inline: 1rem;
      background: var(--white-off);
      border-radius: 5px;
      gap: 0.75rem;

      .item {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-block: 0.5rem;
        padding-inline: 0.75rem;
        background: white;
        border-radius: 5px;

        .item-header {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 0.5rem;
        }

        .item-content {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          border-block-start: 1px solid var(--border);
          padding-block-start: 0.5rem;
        }
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-details {
    .list {
      .header {
        .title {
          font-size: 1rem;
        }
      }

      .items {
        flex-direction: column;

        .item {
          width: 100%;

          .name {
            font-size: 0.875rem;
          }
        }
      }
    }
  }
}
</style>
